import React from 'react';
import './TermsOfUse.css';

const TermsOfUse = () => {
  return (
    <div className="terms-page">
      <div className="terms-container">
        <h1>Website Terms of Use</h1>
        <p className="terms-effective">Version 1.1 &nbsp;|&nbsp; Last updated March 1, 2026</p>

        <section>
          <p>
            These Website Terms of Use (&ldquo;Terms&rdquo;) govern your access to and use of the websites,
            webpages, online forms, downloadable materials, content, and other public-facing online
            resources made available by Bltz AI, Inc. (&ldquo;Bltz AI,&rdquo; &ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;)
            that link to these Terms, including <a href="https://www.bltz.ai">https://www.bltz.ai</a>{' '}
            (collectively, the &ldquo;Website&rdquo;).
          </p>
          <p>
            These Terms apply only to use of the Website. They do not govern access to or use of Bltz AI&rsquo;s
            commercial products, platform, APIs, software, browser extensions, endpoint components,
            professional services, or customer environments. Product and service use is governed by the
            applicable written agreement between Bltz AI and the customer, such as a master services
            agreement, order form, data processing addendum, product terms, statement of work, or other
            written agreement.
          </p>
          <p>By accessing or using the Website, you agree to these Terms. If you do not agree, do not use the Website.</p>
        </section>

        <section>
          <h2>1. Website Use</h2>
          <p>
            You may use the Website only for lawful informational and business purposes. You agree to use
            the Website in accordance with these Terms and all applicable laws, rules, and regulations.
          </p>
          <p>
            You may not use the Website in any manner that could damage, disable, overburden, impair,
            interfere with, or compromise the Website, Company systems, or any other user&rsquo;s access to or
            use of the Website.
          </p>
        </section>

        <section>
          <h2>2. Website Content</h2>
          <p>
            The Website may include text, graphics, logos, trademarks, service marks, product names, images,
            videos, diagrams, blog posts, articles, whitepapers, reports, brochures, downloads, designs,
            layouts, user interfaces, and other materials (&ldquo;Website Content&rdquo;).
          </p>
          <p>
            Website Content is owned by Bltz AI or its licensors and is protected by intellectual property
            and other laws. Except as expressly permitted by these Terms or by written permission from
            Bltz AI, you may not copy, reproduce, modify, distribute, display, publish, mirror, frame,
            scrape, republish, transmit, create derivative works from, or otherwise exploit Website Content.
          </p>
          <p>
            You may download or print publicly available Website Content solely for your internal business
            review, provided that you do not remove proprietary notices or use the content in a misleading
            manner.
          </p>
        </section>

        <section>
          <h2>3. No Product License</h2>
          <p>
            Nothing on the Website grants you any license or right to access or use Bltz AI&rsquo;s commercial
            products, software, APIs, platform, browser extensions, endpoint components, proprietary
            technology, or services.
          </p>
          <p>Any access to Bltz AI products or services is subject to a separate written agreement or authorized product terms.</p>
        </section>

        <section>
          <h2>4. Trademarks</h2>
          <p>
            Bltz AI names, logos, product names, designs, slogans, and related marks are trademarks or
            service marks of Bltz AI or its affiliates. You may not use Bltz AI marks without prior written
            permission.
          </p>
          <p>
            All third-party names, logos, and marks appearing on the Website are the property of their
            respective owners and are used for identification purposes only.
          </p>
        </section>

        <section>
          <h2>5. Prohibited Conduct</h2>
          <p>You may not:</p>
          <p>(a) use the Website for any unlawful, fraudulent, deceptive, harmful, or abusive purpose;</p>
          <p>(b) attempt to gain unauthorized access to the Website, Company systems, accounts, networks, or data;</p>
          <p>(c) probe, scan, test, or attempt to assess the vulnerability of the Website or related systems without prior written authorization;</p>
          <p>(d) interfere with or disrupt the Website, servers, networks, security controls, rate limits, or access controls;</p>
          <p>(e) use bots, crawlers, scrapers, automated tools, or similar methods to access, extract, copy, harvest, or monitor Website Content except as permitted by standard search engine indexing;</p>
          <p>(f) introduce malware, viruses, worms, logic bombs, or other harmful code;</p>
          <p>(g) impersonate any person or entity or misrepresent your affiliation with any person or entity;</p>
          <p>(h) use Website Content to build, train, benchmark, evaluate, or improve a competing product or service;</p>
          <p>(i) remove, obscure, or alter copyright, trademark, or proprietary notices; or</p>
          <p>(j) encourage or assist any third party in doing any of the foregoing.</p>
        </section>

        <section>
          <h2>6. Forms, Submissions, and Communications</h2>
          <p>
            The Website may allow you to submit information through contact forms, demo request forms,
            event forms, newsletter forms, downloadable content forms, or similar channels.
          </p>
          <p>
            You represent that information you submit is accurate and that you have the right to provide it.
            You should not submit confidential, sensitive, regulated, or proprietary information through
            public Website forms unless specifically requested by Bltz AI or covered by a separate written
            agreement.
          </p>
          <p>
            By submitting information through the Website, you authorize Bltz AI to use the information to
            respond to your inquiry, provide requested materials, schedule meetings or demos, operate and
            improve the Website, and communicate with you, subject to applicable privacy notices and laws.
          </p>
        </section>

        <section>
          <h2>7. Feedback</h2>
          <p>
            If you provide ideas, suggestions, comments, or feedback about the Website, Company products,
            or Company services (&ldquo;Feedback&rdquo;), you grant Bltz AI a worldwide, perpetual, irrevocable,
            sublicensable, transferable, royalty-free license to use, reproduce, modify, distribute, and
            otherwise exploit the Feedback without restriction or obligation to you.
          </p>
        </section>

        <section>
          <h2>8. Third-Party Websites and Resources</h2>
          <p>
            The Website may contain links to third-party websites, content, resources, products, or
            services. Bltz AI does not control and is not responsible for third-party websites, content,
            privacy practices, security practices, or terms.
          </p>
          <p>Accessing third-party resources is at your own risk.</p>
        </section>

        <section>
          <h2>9. Informational Content Only</h2>
          <p>
            Website Content is provided for general informational purposes only. It does not constitute
            legal, security, compliance, privacy, procurement, technical, financial, or other professional
            advice.
          </p>
          <p>
            Bltz AI may update, modify, remove, or discontinue Website Content at any time without notice.
            Bltz AI does not guarantee that Website Content is accurate, complete, current, or suitable for
            any particular purpose.
          </p>
        </section>

        <section>
          <h2>10. Forward-Looking Statements</h2>
          <p>
            The Website may include statements about future product plans, features, roadmap items,
            performance, availability, integrations, certifications, compliance activities, or business
            plans. These statements are forward-looking and subject to change.
          </p>
          <p>
            Bltz AI does not commit to deliver any future feature, functionality, certification, service,
            or business outcome unless expressly agreed in a signed written agreement.
          </p>
        </section>

        <section>
          <h2>11. Privacy</h2>
          <p>
            Bltz AI&rsquo;s collection and use of personal information through the Website is described in its
            applicable privacy notice or privacy policy.
          </p>
          <p>
            By using the Website or submitting information through Website forms, you acknowledge that
            Bltz AI may process information in accordance with its applicable privacy documentation.
          </p>
        </section>

        <section>
          <h2>12. Disclaimer of Warranties</h2>
          <p>THE WEBSITE AND WEBSITE CONTENT ARE PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE.&rdquo;</p>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, BLTZ AI DISCLAIMS ALL WARRANTIES, EXPRESS, IMPLIED,
            STATUTORY, OR OTHERWISE, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE, TITLE, NON-INFRINGEMENT, ACCURACY, COMPLETENESS, RELIABILITY, AVAILABILITY, SECURITY,
            AND ERROR-FREE OPERATION.
          </p>
          <p>
            BLTZ AI DOES NOT WARRANT THAT THE WEBSITE WILL BE UNINTERRUPTED, SECURE, ERROR-FREE, OR FREE OF
            VIRUSES OR OTHER HARMFUL COMPONENTS.
          </p>
        </section>

        <section>
          <h2>13. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, BLTZ AI WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
            SPECIAL, CONSEQUENTIAL, EXEMPLARY, PUNITIVE, OR SIMILAR DAMAGES, INCLUDING LOST PROFITS, LOST
            REVENUE, LOSS OF DATA, LOSS OF GOODWILL, BUSINESS INTERRUPTION, OR COST OF SUBSTITUTE SERVICES,
            ARISING OUT OF OR RELATING TO THE WEBSITE, WEBSITE CONTENT, OR THESE TERMS, EVEN IF BLTZ AI HAS
            BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, BLTZ AI WILL NOT BE LIABLE FOR ANY DAMAGES ARISING OUT
            OF OR RELATING TO YOUR ACCESS TO, USE OF, OR INABILITY TO USE THE WEBSITE OR WEBSITE CONTENT,
            INCLUDING ANY RELIANCE ON WEBSITE CONTENT, ERRORS OR OMISSIONS IN WEBSITE CONTENT, UNAUTHORIZED
            ACCESS, SERVICE INTERRUPTION, SECURITY EVENTS, THIRD-PARTY LINKS, OR CONTENT PROVIDED BY THIRD
            PARTIES.
          </p>
          <p>NOTHING IN THESE TERMS LIMITS LIABILITY THAT CANNOT BE LIMITED OR EXCLUDED UNDER APPLICABLE LAW.</p>
        </section>

        <section>
          <h2>14. Indemnification</h2>
          <p>
            You agree to defend, indemnify, and hold harmless Bltz AI and its affiliates, officers,
            directors, employees, contractors, agents, licensors, and service providers from and against
            any claims, damages, liabilities, losses, costs, and expenses, including reasonable attorneys&rsquo;
            fees, arising out of or related to your use of the Website, your violation of these Terms, your
            submissions, or your violation of applicable law or third-party rights.
          </p>
        </section>

        <section>
          <h2>15. Changes to the Website and Terms</h2>
          <p>
            Bltz AI may update, modify, suspend, or discontinue the Website or Website Content at any time
            without notice.
          </p>
          <p>
            Bltz AI may update these Terms from time to time by posting an updated version on the Website.
            The updated Terms will be effective when posted unless a later effective date is stated. Your
            continued use of the Website after updated Terms are posted means you accept the updated Terms.
          </p>
        </section>

        <section>
          <h2>16. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of California, without regard to conflict of
            law principles.
          </p>
          <p>
            Any dispute arising out of or relating to these Terms or the Website will be resolved in the
            state or federal courts located in California, unless otherwise required by applicable law.
          </p>
        </section>

        <section>
          <h2>17. Miscellaneous</h2>
          <p>
            These Terms are the entire agreement between you and Bltz AI regarding your use of the Website
            and supersede prior or contemporaneous understandings regarding the Website.
          </p>
          <p>If any provision of these Terms is held invalid or unenforceable, the remaining provisions will remain in full force and effect.</p>
          <p>Bltz AI&rsquo;s failure to enforce any provision of these Terms is not a waiver.</p>
          <p>You may not assign or transfer these Terms without Bltz AI&rsquo;s prior written consent.</p>
          <p>These Terms do not create any partnership, joint venture, agency, fiduciary, or employment relationship.</p>
          <p>There are no third-party beneficiaries to these Terms.</p>
          <p>Headings are for convenience only and do not affect interpretation.</p>
        </section>

        <section>
          <h2>18. Contact</h2>
          <p>Legal notices or questions about these Terms may be sent to:</p>
          <address className="terms-address">
            Bltz AI, Inc.<br />
            625 Corte Galante<br />
            San Marcos, CA 92069<br />
            Attn: Legal Department<br />
            Email: <a href="mailto:legal@bltz.ai">legal@bltz.ai</a>
          </address>
        </section>
      </div>
    </div>
  );
};

export default TermsOfUse;
